import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { API_ORIGIN, getBrandSettings } from '../services/api';
import { setBrowserNotificationIcon } from '../services/browserNotifications';

export const DEFAULT_BRAND = Object.freeze({
  orgName: 'Royal Medical Center',
  orgShortName: 'RMC',
  productName: 'Incident Reporting System',
  productShortName: 'IRS',
  tagline: 'Occurrence Variance Reporting',
  taglineShort: 'OVR registry',
  refNoPrefix: 'AZG/RMC/OVR/',
  primaryColor: '#9f1733',
  primaryColorDark: '#7f1028',
  accentColor: '#c79a4b',
  logoUrl: '',
  markUrl: '',
  heroUrl: '',
  faviconUrl: '',
});

const FALLBACK_ASSETS = Object.freeze({
  logoUrl: '/logo.png',
  markUrl: '/rmc-mark.png',
  heroUrl: '/irs-hero.png',
  faviconUrl: '/favicon.ico',
});

export const resolveBrandAssetUrl = (url, fallback) => {
  if (!url) return fallback;
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('/uploads')) return `${API_ORIGIN}${url}`;
  return url;
};

const applyBrandToDocument = (brand) => {
  const root = document.documentElement;
  const primary = brand.primaryColor || DEFAULT_BRAND.primaryColor;
  const primaryDark = brand.primaryColorDark || DEFAULT_BRAND.primaryColorDark;
  const accent = brand.accentColor || DEFAULT_BRAND.accentColor;

  root.style.setProperty('--rmc-maroon', primary);
  root.style.setProperty('--rmc-maroon-dark', primaryDark);
  root.style.setProperty('--rmc-gold', accent);
  root.style.setProperty('--bs-primary', primary);
  root.style.setProperty('--bs-link-color', primary);
  root.style.setProperty('--bs-link-hover-color', primaryDark);

  document.title = brand.productName || DEFAULT_BRAND.productName;

  const faviconHref = resolveBrandAssetUrl(brand.faviconUrl, FALLBACK_ASSETS.faviconUrl);
  let link = document.querySelector("link[rel='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconHref;

  setBrowserNotificationIcon(
    resolveBrandAssetUrl(brand.markUrl, FALLBACK_ASSETS.markUrl)
  );
};

const BrandContext = createContext({
  brand: DEFAULT_BRAND,
  loading: true,
  refreshBrand: async () => {},
  setBrandState: () => {},
  assets: FALLBACK_ASSETS,
});

export const BrandProvider = ({ children }) => {
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [loading, setLoading] = useState(true);

  const setBrandState = useCallback((next) => {
    const merged = { ...DEFAULT_BRAND, ...(next || {}) };
    setBrand(merged);
    applyBrandToDocument(merged);
  }, []);

  const refreshBrand = useCallback(async () => {
    try {
      const { data } = await getBrandSettings();
      setBrandState(data);
    } catch (error) {
      setBrandState(DEFAULT_BRAND);
    } finally {
      setLoading(false);
    }
  }, [setBrandState]);

  useEffect(() => {
    refreshBrand();
  }, [refreshBrand]);

  const assets = useMemo(() => ({
    logoUrl: resolveBrandAssetUrl(brand.logoUrl, FALLBACK_ASSETS.logoUrl),
    markUrl: resolveBrandAssetUrl(brand.markUrl, FALLBACK_ASSETS.markUrl),
    heroUrl: resolveBrandAssetUrl(brand.heroUrl, FALLBACK_ASSETS.heroUrl),
    faviconUrl: resolveBrandAssetUrl(brand.faviconUrl, FALLBACK_ASSETS.faviconUrl),
  }), [brand]);

  const value = useMemo(() => ({
    brand,
    loading,
    refreshBrand,
    setBrandState,
    assets,
  }), [assets, brand, loading, refreshBrand, setBrandState]);

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
};

export const useBrand = () => useContext(BrandContext);

export default BrandContext;
